import type { NewsletterIssueData, SpotlightCardBlockProps, SpotlightData } from '../types';
import { theme, sectionColors } from '../theme';
import { pickIssue, safeHttpHref } from './newsletterShared';

// Re-exported: safeHttpHref now lives in newsletterShared so EventsList can guard its event
// link with the same rule. Kept exported here for the callers and tests that already import it.
export { safeHttpHref };

/**
 * An href a visitor may click: absolute, parseable, http(s) only — else null.
 *
 * The spotlight is editor-supplied copy on a PUBLIC page, so an href taken straight from the row
 * is a script/redirect sink: `javascript:alert(1)` executes on click and `data:text/html,...`
 * navigates the tab to attacker markup. The gateway now refuses those schemes on write
 * (routes/newsletter.ts), but rows persisted BEFORE that check still exist and this block also
 * renders directory rows the gateway never validated, so the guard has to live here too. A
 * refused value renders no link at all rather than a dead `#`, which would read as a broken site.
 * Relative paths are refused on purpose: a spotlight link always points at the business's own
 * site, so anything relative is bad data rather than a local link.
 */

/**
 * A local business spotlight (V3.2, LEAD_GEN_MACHINE_BUILD_PLAN.md §14.3): the profile, what
 * locals love, a dofollow link to the business, and the disclosure line when the feature was
 * compensated. Facts come from the directory row (the host / gateway) — nothing is generated
 * here. Framework-agnostic, server-renderable (no state).
 *
 * Data: `spotlight` (BlockData.spotlight, for a block bound to a businessId) or the issue's
 * spotlight section (BlockData.newsletterIssues — by issueId / communitySlug / latest).
 */
export function SpotlightCardBody({ spotlight }: { spotlight: SpotlightData }) {
  const websiteHref = safeHttpHref(spotlight.website_url);
  return (
    <article className="overflow-hidden rounded-2xl border border-black/10" style={{ backgroundColor: theme.surface }} data-business-id={spotlight.business_id}>
      {spotlight.photo_url ? <img src={spotlight.photo_url} alt={spotlight.name} loading="lazy" decoding="async" className="h-56 w-full object-cover" /> : null}
      <div className="p-6">
        <h3 className="text-xl font-semibold" style={{ fontFamily: theme.headingFont }}>{spotlight.name}</h3>
        {spotlight.headline ? <p className="mt-1 font-medium opacity-80">{spotlight.headline}</p> : null}
        <p className="mt-3 leading-relaxed">{spotlight.body}</p>
        {websiteHref ? (
          <a href={websiteHref} target="_blank" rel="noopener" className="mt-4 inline-flex items-center text-sm font-semibold underline" style={{ color: theme.link }}>
            Visit {spotlight.name}
          </a>
        ) : null}
        {spotlight.disclosure ? <p className="mt-4 text-xs opacity-60" data-testid="spotlight-disclosure">{spotlight.disclosure}</p> : null}
      </div>
    </article>
  );
}

export function SpotlightCardBlock(props: SpotlightCardBlockProps & { spotlight?: SpotlightData | null; issues?: NewsletterIssueData[] }) {
  const { heading = 'Local business spotlight', subheading, issues = [] } = props;
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  const fromIssue = pickIssue(issues, { issueId: props.issueId || null, communitySlug: props.communitySlug || null })?.sections.spotlight ?? null;
  const spotlight = props.spotlight ?? fromIssue;
  if (!spotlight) return null;
  return (
    <section style={{ ...colors, fontFamily: theme.bodyFont }} className="px-6 py-12" data-block="spotlight-card">
      <div className="mx-auto max-w-3xl">
        <h2 className="text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>{heading}</h2>
        {subheading ? <p className="mt-2 opacity-80">{subheading}</p> : null}
        <div className="mt-6"><SpotlightCardBody spotlight={spotlight} /></div>
      </div>
    </section>
  );
}
